<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import OwnerDashboard from './OwnerDashboard.vue';
import OmniConversationSidebar from 'dashboard/components/OmniConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    ConversationSidebar,
    OwnerDashboard,
    OmniConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { isAdmin } = useAdmin();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      isAdmin,
    };
  },
  data() {
    return {
      showSearchModal: false,
      selectedAssigneeTab: wootConstants.ASSIGNEE_TYPE.ME,
      customConversationFilter: null,
      isOmniSidebarVisible: true,
      chatListWidth: 340,
      isResizingChatList: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      return !!this.currentChat.id;
    },
    showOwnerDashboard() {
      return this.$route.name === 'home' && this.isAdmin;
    },
    /** Params for omni sidebar breakdown meta (open conversations; assignee_type omitted = all assignees). */
    omniConversationMetaParams() {
      const inboxId = this.inboxId ? Number(this.inboxId) : undefined;
      const teamId = this.teamId ? Number(this.teamId) : undefined;
      return {
        inboxId: Number.isFinite(inboxId) && inboxId > 0 ? inboxId : undefined,
        teamId: Number.isFinite(teamId) && teamId > 0 ? teamId : undefined,
        conversationType: this.conversationType || undefined,
        labels: this.label ? [this.label] : undefined,
        status: 'open',
        includeSidebarBreakdown: true,
      };
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },
  beforeUnmount() {
    this.stopChatListResize();
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    onAssigneeChange(tab) {
      this.selectedAssigneeTab = tab;
    },
    onCustomConversationFilterChange(filter) {
      this.customConversationFilter = filter;
    },
    showOmniSidebar() {
      this.isOmniSidebarVisible = true;
    },
    hideOmniSidebar() {
      this.isOmniSidebarVisible = false;
    },
    startChatListResize(event) {
      this.isResizingChatList = true;
      window.addEventListener('mousemove', this.resizeChatList);
      window.addEventListener('mouseup', this.stopChatListResize);
      event.preventDefault();
    },
    resizeChatList(event) {
      if (!this.isResizingChatList) return;
      const leftOffset = 48 + 216;
      const width = event.clientX - leftOffset;
      this.chatListWidth = Math.min(Math.max(width, 280), 520);
    },
    stopChatListResize() {
      this.isResizingChatList = false;
      window.removeEventListener('mousemove', this.resizeChatList);
      window.removeEventListener('mouseup', this.stopChatListResize);
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <OwnerDashboard v-if="showOwnerDashboard" />
    <OmniConversationSidebar
      v-if="!showOwnerDashboard"
      :is-visible="isOmniSidebarVisible"
      :active-assignee-tab="selectedAssigneeTab"
      :custom-conversation-filter="customConversationFilter"
      :conversation-meta-params="omniConversationMetaParams"
      @assignee-change="onAssigneeChange"
      @collapse="hideOmniSidebar"
      @filter-change="onCustomConversationFilterChange"
    />
    <ChatList
      v-if="!showOwnerDashboard"
      :style="!isOnExpandedLayout ? { width: `${chatListWidth}px` } : null"
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      :selected-assignee-tab="selectedAssigneeTab"
      :custom-conversation-filter="customConversationFilter"
      :is-omni-sidebar-visible="isOmniSidebarVisible"
      @show-omni-sidebar="showOmniSidebar"
      @conversation-load="onConversationLoad"
    />
    <div
      v-if="
        !showOwnerDashboard &&
        showConversationList &&
        showMessageView &&
        !isOnExpandedLayout
      "
      class="hidden w-1 cursor-col-resize bg-transparent hover:bg-n-brand md:block"
      @mousedown="startChatListResize"
    />
    <ConversationBox
      v-if="!showOwnerDashboard && showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <ConversationSidebar
        v-if="!showOwnerDashboard && shouldShowSidebar"
        :current-chat="currentChat"
      />
    </ConversationBox>
    <CmdBarConversationSnooze />
  </section>
</template>
